/**
 * Prompt Editor Modal
 *
 * Modal for editing prompt configuration including prompts and model settings.
 */

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal,
  Textarea,
  Select,
  Button,
  Group,
  Stack,
  Tabs,
  Text,
  Badge,
  Alert,
  Code,
  Slider,
  NumberInput,
  Paper,
  Divider,
  Tooltip,
  Loader
} from '@mantine/core';
import {
  IconDeviceFloppy,
  IconRefresh,
  IconPlayerPlay,
  IconAlertCircle,
  IconCheck,
  IconInfoCircle,
  IconBrain
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

import {
  savePrompt,
  resetPromptToDefault,
  testPrompt,
  selectPromptsSaving,
  selectPromptsTesting,
  selectPromptTestResult,
  selectActiveModels,
  selectPromptPermissions,
  clearPromptTestResult
} from '../../redux/slices/promptsSlice';

// Extract template variables from a prompt string
const extractVariables = (template) => {
  if (!template) return [];
  const matches = template.match(/\{\{(\w+)\}\}/g) || [];
  return [...new Set(matches.map(m => m.replace(/\{\{|\}\}/g, '')))];
};

const PromptEditorModal = ({ prompt, opened, onClose }) => {
  const dispatch = useDispatch();

  // Redux state
  const saving = useSelector(selectPromptsSaving);
  const testing = useSelector(selectPromptsTesting);
  const testResult = useSelector((state) =>
    prompt ? selectPromptTestResult(state, prompt.promptKey) : null
  );
  const models = useSelector(selectActiveModels);
  const permissions = useSelector(selectPromptPermissions);

  // Local state
  const [activeTab, setActiveTab] = useState('prompts');
  const [formData, setFormData] = useState({
    systemPrompt: '',
    userPromptTemplate: '',
    modelId: '',
    modelConfig: {
      temperature: 0.7,
      max_tokens: 1024
    },
    description: ''
  });
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form data when prompt changes
  useEffect(() => {
    if (prompt) {
      setFormData({
        systemPrompt: prompt.systemPrompt || '',
        userPromptTemplate: prompt.userPromptTemplate || '',
        modelId: prompt.modelId || 'gpt-5-nano',
        modelConfig: {
          temperature: prompt.modelConfig?.temperature ?? 0.7,
          max_tokens: prompt.modelConfig?.max_tokens || prompt.modelConfig?.max_output_tokens || 1024,
          frequency_penalty: prompt.modelConfig?.frequency_penalty ?? 0,
          presence_penalty: prompt.modelConfig?.presence_penalty ?? 0
        },
        description: prompt.description || ''
      });
      setHasChanges(false);
    }
  }, [prompt]);

  // Clear test result when modal closes
  useEffect(() => {
    if (!opened && prompt) {
      dispatch(clearPromptTestResult({ promptKey: prompt.promptKey }));
    }
  }, [opened, prompt, dispatch]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleConfigChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      modelConfig: { ...prev.modelConfig, [field]: value }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!prompt || !permissions.canEdit) return;

    try {
      await dispatch(savePrompt({
        promptKey: prompt.promptKey,
        systemPrompt: formData.systemPrompt,
        userPromptTemplate: formData.userPromptTemplate,
        modelId: formData.modelId,
        modelConfig: formData.modelConfig,
        description: formData.description
      })).unwrap();

      notifications.show({
        title: 'Prompt saved',
        message: `${prompt.promptName} has been customized`,
        color: 'green',
        icon: <IconCheck size={16} />
      });

      setHasChanges(false);
    } catch (err) {
      notifications.show({
        title: 'Error saving prompt',
        message: err.message || 'Failed to save',
        color: 'red',
        icon: <IconAlertCircle size={16} />
      });
    }
  };

  const handleReset = async () => {
    if (!prompt || !permissions.canEdit) return;

    try {
      const result = await dispatch(resetPromptToDefault(prompt.promptKey)).unwrap();

      // Update form with default values
      if (result.prompt) {
        setFormData({
          systemPrompt: result.prompt.systemPrompt || '',
          userPromptTemplate: result.prompt.userPromptTemplate || '',
          modelId: result.prompt.modelId || 'gpt-5-nano',
          modelConfig: result.prompt.modelConfig || { temperature: 0.7, max_tokens: 1024 },
          description: result.prompt.description || ''
        });
      }

      notifications.show({
        title: 'Reset to default',
        message: `${prompt.promptName} has been reset to default settings`,
        color: 'blue',
        icon: <IconRefresh size={16} />
      });

      setHasChanges(false);
    } catch (err) {
      notifications.show({
        title: 'Error resetting prompt',
        message: err.message || 'Failed to reset',
        color: 'red',
        icon: <IconAlertCircle size={16} />
      });
    }
  };

  const handleTest = async () => {
    if (!prompt) return;

    try {
      await dispatch(testPrompt({
        promptKey: prompt.promptKey,
        useCustomization: true
      })).unwrap();
    } catch (err) {
      notifications.show({
        title: 'Test failed',
        message: err.message || 'Failed to test prompt',
        color: 'red',
        icon: <IconAlertCircle size={16} />
      });
    }
  };

  if (!prompt) return null;

  const isCustomized = prompt.resolvedFrom === 'user_customization';
  const systemVariables = extractVariables(formData.systemPrompt);
  const userVariables = extractVariables(formData.userPromptTemplate);
  const allVariables = [...new Set([...systemVariables, ...userVariables])];

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group>
          <IconBrain size={20} />
          <Text fw={600}>{prompt.promptName}</Text>
          {isCustomized && (
            <Badge color="blue" size="sm">Customized</Badge>
          )}
        </Group>
      }
      size="xl"
      padding="lg"
    >
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List mb="md">
          <Tabs.Tab value="prompts">Prompts</Tabs.Tab>
          <Tabs.Tab value="model">Model Settings</Tabs.Tab>
          <Tabs.Tab value="test">Test</Tabs.Tab>
        </Tabs.List>

        {/* Prompts Tab */}
        <Tabs.Panel value="prompts">
          <Stack gap="md">
            <div>
              <Group justify="space-between" mb={4}>
                <Text size="sm" fw={500}>System Prompt</Text>
                <Text size="xs" c="dimmed">
                  {formData.systemPrompt.length} chars
                </Text>
              </Group>
              <Textarea
                value={formData.systemPrompt}
                onChange={(e) => handleFieldChange('systemPrompt', e.currentTarget.value)}
                placeholder="System prompt defining the prompt's role..."
                minRows={6}
                maxRows={12}
                autosize
                disabled={!permissions.canEdit}
                styles={{
                  input: { fontFamily: 'monospace', fontSize: '12px' }
                }}
              />
            </div>

            <div>
              <Group justify="space-between" mb={4}>
                <Text size="sm" fw={500}>User Prompt Template</Text>
                <Text size="xs" c="dimmed">
                  {formData.userPromptTemplate.length} chars
                </Text>
              </Group>
              <Textarea
                value={formData.userPromptTemplate}
                onChange={(e) => handleFieldChange('userPromptTemplate', e.currentTarget.value)}
                placeholder="User prompt template with {{variables}}..."
                minRows={8}
                maxRows={16}
                autosize
                disabled={!permissions.canEdit}
                styles={{
                  input: { fontFamily: 'monospace', fontSize: '12px' }
                }}
              />
            </div>

            {/* Available variables */}
            {allVariables.length > 0 && (
              <Paper p="sm" withBorder>
                <Group gap="xs" align="flex-start">
                  <IconInfoCircle size={16} style={{ marginTop: 2 }} />
                  <div>
                    <Text size="xs" fw={500} mb={4}>Available Variables</Text>
                    <Group gap={4}>
                      {allVariables.map(v => (
                        <Code key={v} size="xs">{`{{${v}}}`}</Code>
                      ))}
                    </Group>
                  </div>
                </Group>
              </Paper>
            )}
          </Stack>
        </Tabs.Panel>

        {/* Model Settings Tab */}
        <Tabs.Panel value="model">
          <Stack gap="md">
            <Select
              label="Model"
              description="Select the AI model to use for this prompt"
              value={formData.modelId}
              onChange={(value) => handleFieldChange('modelId', value)}
              data={models.map(m => ({
                value: m.id,
                label: `${m.name} (${m.speed}, ${m.cost})`
              }))}
              disabled={!permissions.canEdit}
            />

            <div>
              <Text size="sm" fw={500} mb={8}>Temperature</Text>
              <Slider
                value={formData.modelConfig.temperature}
                onChange={(value) => handleConfigChange('temperature', value)}
                min={0}
                max={1}
                step={0.1}
                marks={[
                  { value: 0, label: '0' },
                  { value: 0.5, label: '0.5' },
                  { value: 1, label: '1' }
                ]}
                disabled={!permissions.canEdit}
              />
              <Text size="xs" c="dimmed" mt={4}>
                Lower = more focused, Higher = more creative
              </Text>
            </div>

            <NumberInput
              label="Max Tokens"
              description="Maximum tokens in the response"
              value={formData.modelConfig.max_tokens}
              onChange={(value) => handleConfigChange('max_tokens', value)}
              min={256}
              max={8192}
              step={256}
              disabled={!permissions.canEdit}
            />

            <Group grow>
              <div>
                <Text size="sm" fw={500} mb={8}>Frequency Penalty</Text>
                <Slider
                  value={formData.modelConfig.frequency_penalty || 0}
                  onChange={(value) => handleConfigChange('frequency_penalty', value)}
                  min={0}
                  max={2}
                  step={0.1}
                  disabled={!permissions.canEdit}
                />
              </div>
              <div>
                <Text size="sm" fw={500} mb={8}>Presence Penalty</Text>
                <Slider
                  value={formData.modelConfig.presence_penalty || 0}
                  onChange={(value) => handleConfigChange('presence_penalty', value)}
                  min={0}
                  max={2}
                  step={0.1}
                  disabled={!permissions.canEdit}
                />
              </div>
            </Group>

            <Textarea
              label="Description"
              description="Brief description of what this prompt does"
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.currentTarget.value)}
              minRows={2}
              disabled={!permissions.canEdit}
            />
          </Stack>
        </Tabs.Panel>

        {/* Test Tab */}
        <Tabs.Panel value="test">
          <Stack gap="md">
            <Alert color="blue" icon={<IconInfoCircle size={16} />}>
              Test this prompt with sample input to see how it performs with your current settings.
            </Alert>

            <Button
              leftSection={testing ? <Loader size={14} /> : <IconPlayerPlay size={16} />}
              onClick={handleTest}
              disabled={testing}
              variant="light"
            >
              {testing ? 'Running test...' : 'Run Test'}
            </Button>

            {testResult && (
              <Paper p="md" withBorder>
                {testResult.loading ? (
                  <Group justify="center" py="md">
                    <Loader size="sm" />
                    <Text size="sm">Running test...</Text>
                  </Group>
                ) : testResult.error ? (
                  <Alert color="red" icon={<IconAlertCircle size={16} />}>
                    {testResult.error}
                  </Alert>
                ) : (
                  <Stack gap="sm">
                    <Group justify="space-between">
                      <Text size="sm" fw={500}>Test Result</Text>
                      <Badge color="green" size="sm">
                        {testResult.metrics?.duration} | {testResult.metrics?.tokensUsed} tokens
                      </Badge>
                    </Group>
                    <Divider />
                    <div>
                      <Text size="xs" c="dimmed" mb={4}>Output:</Text>
                      <Paper p="sm" bg="gray.0" style={{ maxHeight: 300, overflow: 'auto' }}>
                        <Text size="xs" style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                          {testResult.result}
                        </Text>
                      </Paper>
                    </div>
                  </Stack>
                )}
              </Paper>
            )}
          </Stack>
        </Tabs.Panel>
      </Tabs>

      <Divider my="md" />

      {/* Actions */}
      <Group justify="space-between">
        <Group>
          {permissions.canEdit && isCustomized && (
            <Tooltip label="Reset to default configuration">
              <Button
                variant="subtle"
                color="gray"
                leftSection={<IconRefresh size={16} />}
                onClick={handleReset}
                loading={saving}
              >
                Reset to Default
              </Button>
            </Tooltip>
          )}
        </Group>
        <Group>
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          {permissions.canEdit && (
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={handleSave}
              loading={saving}
              disabled={!hasChanges}
            >
              Save Changes
            </Button>
          )}
        </Group>
      </Group>
    </Modal>
  );
};

export default PromptEditorModal;
